import { useEffect, useRef, type Ref } from 'react'
import introScreen from 'url:asset/launchpad/launchpad-intro-screen.jpg'
import iphoneFrame from 'url:asset/launchpad/iphone-18-pro-black-frame.png'
import sermonThumbnail from 'url:asset/launchpad/intro/sermon-thumbnail.jpg'
import appleMusic from 'url:asset/launchpad/intro/apple-music.png'
import spotify from 'url:asset/launchpad/intro/spotify.png'
import tidal from 'url:asset/launchpad/intro/tidal.png'
import youtubeMusic from 'url:asset/launchpad/intro/youtube-music.png'

// Launchpad Intro (Figma 3245:2430), animating in from "Launchpad Header - Pre Animation" (3275:731).
// The cards start around the phone; on scroll they fly in and the phone scales + fades in.
//
// Variants:
//   morph    – each card flies to the matching spot on the phone's screen
//   converge – both cards pull into the middle of the phone and shrink away
// Try one without editing code with ?intro=converge or ?intro=morph
type IntroVariant = 'morph' | 'converge'
const DEFAULT_VARIANT: IntroVariant = 'morph'

// Everything is laid out in Figma's coordinates on a 654×552 stage (the phone row)
const STAGE = { w: 654, h: 552 }
const PHONE = { x: 192, y: 0, w: 270, h: 552 }
const SERMON = { x: 309, y: 50, w: 286, h: 248 }
const WORSHIP = { x: 114, y: 266, w: 180, h: 208 }

// Where each card's content appears in the phone's screenshot, in stage coordinates
const SERMON_ON_SCREEN = { x: PHONE.x + 24, y: PHONE.y + 121, w: 222 }
const WORSHIP_ON_SCREEN = { x: PHONE.x + 24, y: PHONE.y + 399, w: 110 }

const CARDS_MS = 900
const PHONE_DELAY_MS = 300
const PHONE_MS = 750
const TRIGGER_VISIBLE = 0.6

type Box = { x: number, y: number, w: number }

// Transform (with a top-left origin) that moves and scales `from` onto `to`
function landOn(from: Box, to: Box) {
  const s = to.w / from.w
  return `translate(${to.x - from.x}px, ${to.y - from.y}px) scale(${s})`
}

function shrinkInto(from: Box & { h: number }, scale: number) {
  const cx = PHONE.x + PHONE.w / 2
  const cy = PHONE.y + PHONE.h / 2
  return landOn(from, { x: cx - (from.w * scale) / 2, y: cy - (from.h * scale) / 2, w: from.w * scale })
}

export default function LaunchpadIntro() {
  const stageRef = useRef<HTMLDivElement>(null)
  const sermonRef = useRef<HTMLDivElement>(null)
  const worshipRef = useRef<HTMLDivElement>(null)
  const phoneRef = useRef<HTMLDivElement>(null)

  const play = () => {
    const sermon = sermonRef.current
    const worship = worshipRef.current
    const phone = phoneRef.current
    if (!sermon || !worship || !phone) return

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      sermon.style.opacity = worship.style.opacity = '0'
      phone.style.opacity = '1'
      phone.style.transform = 'none'
      return
    }

    const variant = new URLSearchParams(window.location.search).get('intro') as IntroVariant | null ?? DEFAULT_VARIANT
    const sermonEnd = variant === 'morph' ? landOn(SERMON, SERMON_ON_SCREEN) : shrinkInto(SERMON, 0.35)
    const worshipEnd = variant === 'morph' ? landOn(WORSHIP, WORSHIP_ON_SCREEN) : shrinkInto(WORSHIP, 0.35)

    const cardFrames = (end: string): Keyframe[] => [
      { transform: 'none', opacity: 1 },
      { opacity: 1, offset: variant === 'morph' ? 0.6 : 0.4 },
      { transform: end, opacity: 0 },
    ]
    const cardTiming: KeyframeAnimationOptions = { duration: CARDS_MS, easing: 'cubic-bezier(0.65, 0, 0.35, 1)', fill: 'forwards' }

    sermon.animate(cardFrames(sermonEnd), cardTiming)
    worship.animate(cardFrames(worshipEnd), { ...cardTiming, delay: 60 })
    phone.animate(
      [
        { opacity: 0, transform: 'scale(0.85)' },
        { opacity: 1, transform: 'scale(1)' },
      ],
      { duration: PHONE_MS, delay: PHONE_DELAY_MS, easing: 'cubic-bezier(0.22, 1, 0.36, 1)', fill: 'forwards' },
    )
  }

  useEffect(() => {
    const stage = stageRef.current
    if (!stage) return

    const observer = new IntersectionObserver(([entry]) => {
      if (!entry.isIntersecting) return
      observer.disconnect()
      play()
    }, { threshold: TRIGGER_VISIBLE })

    observer.observe(stage)
    return () => observer.disconnect()
  }, [])

  return (
    <div className="max-w-[654px] mx-auto px-8 md:px-0 mb-20 md:mb-24 flex flex-col items-center gap-6 text-center overflow-x-clip">
      <div className="flex flex-col items-center font-semibold leading-tight md:leading-none">
        <p className="text-2xl md:text-[36px]">Introducing Launchpad.</p>
        <h1 className="text-3xl md:text-[42px] text-[#ffa8a8]">Meet them where they're at.</h1>
      </div>

      {/* The stage is laid out at Figma size and scaled down on small screens */}
      <div ref={stageRef} className="relative w-[523px] h-[442px] sm:w-[654px] sm:h-[552px]">
        <div
          className="absolute top-0 left-0 origin-top-left scale-80 sm:scale-100 text-left"
          style={{ width: STAGE.w, height: STAGE.h }}
        >
          <div
            ref={phoneRef}
            className="absolute"
            style={{ left: PHONE.x, top: PHONE.y, width: PHONE.w, height: PHONE.h, opacity: 0, transform: 'scale(0.85)' }}
          >
            <img
              src={introScreen}
              className="absolute left-[14.4px] top-[13.8px] w-[241.2px] h-[524.4px] rounded-[14px] object-cover"
              draggable={false}
            />
            <img src={iphoneFrame} className="absolute inset-0 size-full" draggable={false} />
          </div>

          <WorshipSongsCard cardRef={worshipRef} />
          <FeaturedSermonCard cardRef={sermonRef} />
        </div>
      </div>

      <p className="font-semibold text-xl md:text-2xl leading-[1.2] text-[#888888]">
        Launchpad brings your latest service, worship songs, and events to every member of your church.
      </p>
    </div>
  )
}

const cardClass = 'absolute bg-white rounded-[24px] overflow-hidden origin-top-left drop-shadow-[0px_0px_6px_rgba(0,0,0,0.1)]'

function FeaturedSermonCard({ cardRef }: { cardRef: Ref<HTMLDivElement> }) {
  return (
    <div ref={cardRef} className={cardClass} style={{ left: SERMON.x, top: SERMON.y, width: SERMON.w, height: SERMON.h }}>
      <div className="absolute top-0 left-0 w-[286px] h-[161px]">
        <img src={sermonThumbnail} className="absolute inset-0 size-full object-cover" draggable={false} />
        <div className="absolute inset-0 bg-gradient-to-b from-[rgba(0,0,0,0)] to-[rgba(0,0,0,0.6)]" />
      </div>

      <div className="absolute left-[14px] top-[118px] flex flex-col font-semibold leading-[1.3]">
        <p className="text-[#c5c5c5] text-[10px]">FEATURED SERMON</p>
        <p className="text-white text-[16px] whitespace-nowrap">Sent By Jesus | Seek First</p>
      </div>

      <p className="absolute left-[14px] top-[170px] w-[258px] text-[12px] leading-[1.3] text-black">
        What is the main thing Jesus has called us to do? In Acts 1, Jesus sends His followers to be His witnesses—from their own neighborhoods to the ends of the earth. In this message...
      </p>
    </div>
  )
}

function WorshipSongsCard({ cardRef }: { cardRef: Ref<HTMLDivElement> }) {
  const icons = [[appleMusic, spotify], [tidal, youtubeMusic]]

  return (
    <div ref={cardRef} className={cardClass} style={{ left: WORSHIP.x, top: WORSHIP.y, width: WORSHIP.w, height: WORSHIP.h }}>
      <p className="absolute left-[18px] top-[16px] flex items-center gap-1 font-semibold text-[12px] leading-[1.3] text-[#8d8d8d] whitespace-nowrap">
        <MusicNote className="h-[11px] w-auto" />
        Worship Songs
      </p>

      <div className="absolute inset-[22.12%_10%_8.65%_10%] flex gap-[11.52px] items-start justify-center">
        {icons.map((column, i) => (
          <div key={i} className="flex flex-1 flex-col gap-[11.52px]">
            {column.map(icon => (
              <img
                key={icon}
                src={icon}
                className="w-full aspect-square rounded-[14.4px] shadow-[0px_0px_17.28px_0px_rgba(0,0,0,0.1)]"
                draggable={false}
              />
            ))}
          </div>
        ))}
      </div>
    </div>
  )
}

function MusicNote({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 12 16" className={className} fill="currentColor">
      <path d="M11 0.5v10.2a2.8 2.8 0 1 1-1.6-2.53V4.1L4.6 5.2v7.5a2.8 2.8 0 1 1-1.6-2.53V2.6L11 0.5z" />
    </svg>
  )
}
